<template>
  <div class="invoice-web-view">
    <div v-if="loading" class="text-center py-5">
      <i class="fas fa-spinner fa-spin fa-3x mb-3"></i>
      <p>Loading invoice...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <div class="alert alert-danger">
        <i class="fas fa-exclamation-triangle me-2"></i>
        {{ error }}
      </div>
    </div>

    <div v-else-if="invoice" class="container py-4">
      <!-- Payment Status Banner -->
      <div v-if="invoice.IsPaid" class="alert alert-success mb-4">
        <i class="fas fa-check-circle me-2"></i>
        <strong>This invoice has been paid.</strong>
        Thank you for your payment!
      </div>
      
      <!-- Payment Button -->
      <div v-else-if="!paymentProcessing" class="text-center mb-4">
        <button 
          class="btn btn-lg btn-primary"
          @click="initiatePayment"
          :disabled="paymentLoading"
        >
          <CreditCard :size="20" class="me-2" />
          {{ paymentLoading ? 'Processing...' : 'Click Here to Pay Your Invoice' }}
        </button>
      </div>

      <!-- Invoice Display -->
      <div class="invoice-container card">
        <div class="card-body">
          <!-- Company Header -->
          <div class="text-center mb-4">
            <img 
              v-if="companyLogo" 
              :src="companyLogo" 
              alt="Company Logo"
              class="mb-3"
              style="max-width: 200px;"
            />
            <h2>{{ companyName || 'Invoice' }}</h2>
            <p class="text-muted mb-0">{{ companyAddress }}</p>
            <p class="text-muted">{{ companyPhone }} | {{ companyEmail }}</p>
          </div>

          <hr />

          <!-- Invoice Info -->
          <div class="row mb-4">
            <div class="col-md-6">
              <h5>Invoice To:</h5>
              <p class="mb-0"><strong>{{ invoice.CustomerName }}</strong></p>
              <p v-if="invoice.CustomerAddress" class="mb-0">{{ invoice.CustomerAddress }}</p>
            </div>
            <div class="col-md-6 text-md-end">
              <p class="mb-1"><strong>Invoice #:</strong> {{ invoice.InvoiceNumber }}</p>
              <p class="mb-1"><strong>Date:</strong> {{ formatDate(invoice.CreateDate) }}</p>
              <p v-if="invoice.DueDate" class="mb-1"><strong>Due Date:</strong> {{ formatDate(invoice.DueDate) }}</p>
            </div>
          </div>

          <!-- Line Items -->
          <div class="table-responsive mb-4">
            <table class="table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th class="text-end">Qty</th>
                  <th class="text-end">Unit Price</th>
                  <th class="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in invoice.Items" :key="item.Id">
                  <td>{{ item.Description }}</td>
                  <td class="text-end">{{ item.Quantity }}</td>
                  <td class="text-end">{{ formatCurrency(item.UnitCost) }}</td>
                  <td class="text-end">{{ formatCurrency(item.LineTotal) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="3" class="text-end"><strong>Subtotal:</strong></td>
                  <td class="text-end">{{ formatCurrency(invoice.Subtotal) }}</td>
                </tr>
                <tr v-if="invoice.Tax">
                  <td colspan="3" class="text-end"><strong>Tax:</strong></td>
                  <td class="text-end">{{ formatCurrency(invoice.Tax) }}</td>
                </tr>
                <tr class="table-active">
                  <td colspan="3" class="text-end"><strong>Total:</strong></td>
                  <td class="text-end"><strong>{{ formatCurrency(invoice.Total) }}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <!-- Notes -->
          <div v-if="invoice.Notes" class="mt-4">
            <h6>Notes:</h6>
            <p class="text-muted">{{ invoice.Notes }}</p>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="text-center mt-4 text-muted">
        <small>Thank you for your business!</small>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { CreditCard } from 'lucide-vue-next';
import ApiService from '@/services/ApiService';
import Alert from '@/services/Alert';
import { useFormatting } from '@/composables';

const route = useRoute();
const { formatCurrency, formatDate } = useFormatting();

const loading = ref(true);
const paymentLoading = ref(false);
const paymentProcessing = ref(false);
const error = ref('');
const invoice = ref<any>(null);

// Company settings
const companyLogo = ref('');
const companyName = ref('');
const companyAddress = ref('');
const companyPhone = ref('');
const companyEmail = ref('');

const loadInvoice = async () => {
  try {
    loading.value = true;
    const invoiceId = route.params.invoiceId;
    const secureHash = route.params.hash;

    if (!invoiceId || !secureHash) {
      error.value = 'Invalid invoice link. Please check the URL and try again.';
      return;
    }

    // Load invoice with hash validation
    const data = await ApiService.getInvoiceByHash(invoiceId, secureHash);
    
    if (!data) {
      error.value = 'Invoice not found or link has expired.';
      return;
    }

    invoice.value = data;

    // Load company settings
    const companySettings = await ApiService.getOptions('Company');
    companyLogo.value = companySettings.find((o: any) => o.Key === 'CompanyLogo')?.Value || '';
    companyName.value = companySettings.find((o: any) => o.Key === 'CompanyName')?.Value || '';
    companyAddress.value = companySettings.find((o: any) => o.Key === 'CompanyAddress')?.Value || '';
    companyPhone.value = companySettings.find((o: any) => o.Key === 'CompanyPhone')?.Value || '';
    companyEmail.value = companySettings.find((o: any) => o.Key === 'CompanyEmail')?.Value || '';

  } catch (err: any) {
    console.error('Error loading invoice:', err);
    error.value = err.message || 'Failed to load invoice. Please contact support.';
  } finally {
    loading.value = false;
  }
};

const initiatePayment = async () => {
  try {
    paymentLoading.value = true;
    
    // Call backend to create Square checkout session
    const response = await ApiService.createSquareCheckout(invoice.value.Id);
    
    if (response?.Redirect) {
      // Redirect to Square checkout page
      window.location.href = response.Redirect;
    } else {
      Alert.notify({ text: 'Failed to initiate payment. Please try again.', type: 'error' });
    }
  } catch (err: any) {
    console.error('Error initiating payment:', err);
    Alert.notify({ text: err.message || 'Failed to initiate payment', type: 'error' });
  } finally {
    paymentLoading.value = false;
  }
};

onMounted(() => {
  loadInvoice();
});
</script>

<style scoped>
.invoice-web-view {
  min-height: 100vh;
  background-color: #f8f9fa;
}

.invoice-container {
  max-width: 800px;
  margin: 0 auto;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
}

@media print {
  .alert,
  button {
    display: none;
  }
}
</style>
